import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { NUEVA_OPORTUNIDAD, evaluarAsignacion } from "../../utils/oportunidades/asignacion";
import {
  asignarCotizacionAOportunidad,
  getOportunidadesAbiertasDelCliente,
} from "../../utils/oportunidades/ordenamiento";
import styles from "./AsignarOportunidadModal.module.scss";

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

/**
 * Modal "Asignar a oportunidad" (fase 2 del diseño de oportunidades — herramienta de ordenamiento
 * de Nico y Karin). Mueve la cotización a otra oportunidad abierta del MISMO cliente y país, a una
 * nueva, o renombra la elegida. La escritura es una sola RPC atómica; este modal solo arma la
 * elección, muestra antes el efecto y valida lo mismo que la RPC (que es la que manda). La
 * decisión vive en `evaluarAsignacion` (src/utils/oportunidades/asignacion.js), probada aparte.
 *
 * Props:
 *  - isOpen, onClose
 *  - cotizacion: la fila de Detalle (sale o contrato) con oportunidad_id, client_id, pais,
 *    estatus, campana, code, cliente, type.
 *  - nombreActual: nombre de su oportunidad actual (para precargar el renombre).
 *  - onAsignada(resultado): recibe el estado final que devuelve la RPC; el padre parchea sus
 *    filas (10.234) y cierra.
 */
export const AsignarOportunidadModal = ({ isOpen, onClose, cotizacion, nombreActual, onAsignada }) => {
  const [opciones, setOpciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [seleccion, setSeleccion] = useState(null);
  const [nombreNueva, setNombreNueva] = useState("");
  const [nombreEditado, setNombreEditado] = useState("");
  const [guardando, setGuardando] = useState(false);

  const actualId = cotizacion?.oportunidad_id != null ? Number(cotizacion.oportunidad_id) : null;

  useEffect(() => {
    if (!isOpen || !cotizacion) return undefined;
    let vigente = true;
    setCargando(true);
    setErrorCarga(null);
    setOpciones([]);
    setSeleccion(actualId);
    setNombreNueva((cotizacion.campana || "").trim());
    setNombreEditado(nombreActual || "");
    getOportunidadesAbiertasDelCliente({ clientId: cotizacion.client_id, pais: cotizacion.pais }).then(
      ({ data, error }) => {
        if (!vigente) return;
        if (error) setErrorCarga(error);
        else setOpciones(data);
        setCargando(false);
      }
    );
    return () => {
      vigente = false;
    };
  }, [isOpen, cotizacion, actualId, nombreActual]);

  const evaluacion = useMemo(
    () => evaluarAsignacion({ cotizacion, opciones, seleccion, nombreNueva, nombreEditado }),
    [cotizacion, opciones, seleccion, nombreNueva, nombreEditado]
  );
  const { esNueva, elegida, actual, sinCambios, errorNombre, efectos, payload } = evaluacion;

  const puedeConfirmar = !cargando && !guardando && !errorCarga && !!payload;

  const confirmar = async () => {
    if (!puedeConfirmar) return;
    setGuardando(true);
    const { data, error } = await asignarCotizacionAOportunidad(payload);
    setGuardando(false);
    if (error) {
      toast.error(error);
      return;
    }
    onAsignada(data);
  };

  const elegir = (id) => {
    setSeleccion(id);
    if (id !== NUEVA_OPORTUNIDAD) {
      const o = opciones.find((x) => Number(x.oportunidad_id) === id);
      setNombreEditado(o ? o.nombre : "");
    }
  };

  if (!cotizacion) return null;

  return (
    <Modal isOpen={isOpen} toggle={guardando ? undefined : onClose} centered contentClassName={styles.contenido}>
      <ModalHeader toggle={guardando ? undefined : onClose}>
        Asignar a oportunidad
        <div className={styles.subtitulo}>
          {cotizacion.cliente} · {cotizacion.campana ? cotizacion.campana.toUpperCase() : "sin campaña"}
          {cotizacion.code ? ` · ${cotizacion.code}` : ""} · {cotizacion.estatus}
        </div>
      </ModalHeader>
      <ModalBody className={styles.cuerpo}>
        {cargando && <div className={styles.aviso}>Cargando las oportunidades del cliente…</div>}
        {errorCarga && <div className={styles.error}>No se pudieron cargar las oportunidades: {errorCarga}</div>}
        {!cargando && !errorCarga && (
          <>
            {!actual && (
              <div className={styles.error}>
                La oportunidad actual de esta cotización no figura entre las abiertas del cliente. Revisalo antes de
                moverla.
              </div>
            )}
            <div className={styles.lista} role="radiogroup" aria-label="Oportunidades del cliente">
              {opciones.map((o) => {
                const id = Number(o.oportunidad_id);
                return (
                  <label key={id} className={`${styles.opcion} ${seleccion === id ? styles.opcionActiva : ""}`}>
                    <input
                      type="radio"
                      name="oportunidad-destino"
                      checked={seleccion === id}
                      onChange={() => elegir(id)}
                    />
                    <span className={styles.opcionNombre}>{o.nombre}</span>
                    {id === actualId && <span className={styles.actual}>actual</span>}
                    <span className={styles.estatus}>{o.estatus}</span>
                    <span className={styles.cantidad}>
                      {plural(Number(o.n_cotizaciones) || 0, "cotización", "cotizaciones")}
                    </span>
                  </label>
                );
              })}
              <label className={`${styles.opcion} ${esNueva ? styles.opcionActiva : ""}`}>
                <input
                  type="radio"
                  name="oportunidad-destino"
                  checked={esNueva}
                  onChange={() => elegir(NUEVA_OPORTUNIDAD)}
                />
                <span className={styles.opcionNombre}>Nueva oportunidad</span>
              </label>
            </div>

            {(esNueva || elegida) && (
              <div className={styles.campo}>
                <label htmlFor="nombre-oportunidad">
                  {esNueva ? "Nombre de la oportunidad nueva" : "Nombre (podés renombrarla)"}
                </label>
                <input
                  id="nombre-oportunidad"
                  type="text"
                  className="form-control"
                  value={esNueva ? nombreNueva : nombreEditado}
                  onChange={(e) => (esNueva ? setNombreNueva(e.target.value) : setNombreEditado(e.target.value))}
                  maxLength={120}
                />
              </div>
            )}

            {errorNombre && <div className={styles.error}>{errorNombre}</div>}
            {!errorNombre && efectos.length > 0 && (
              <ul className={styles.efectos}>
                {efectos.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            {!errorNombre && sinCambios && (
              <div className={styles.aviso}>Elegí otra oportunidad, una nueva, o cambiá el nombre.</div>
            )}
          </>
        )}

        <div className={styles.botones}>
          <Button color="secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button color="primary" onClick={confirmar} disabled={!puedeConfirmar}>
            {guardando ? "Guardando…" : "Confirmar"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};
